import { DASHBOARD_ASSETS } from '../fixtures';
import type {
  DashboardAsset,
  DashboardAssetReadinessStatus,
  DashboardAssetType,
} from '../types.js';

export interface DashboardAssetInventory {
  assets: DashboardAsset[];
  countsByType: Record<DashboardAssetType, number>;
  countsByReadiness: Record<DashboardAssetReadinessStatus, number>;
  storageUsedLabel: string;
  totalAssetsLabel: string;
}

type DashboardAssetRecord = Partial<DashboardAsset> & {
  id?: string;
  name?: string;
  type?: string;
  sizeLabel?: string;
  updatedAtLabel?: string;
  description?: string;
  metadata?: Array<{ label?: string; value?: string }> | null;
};

const SUPPORTED_READINESS = new Set<DashboardAssetReadinessStatus>([
  "staged",
  "processing",
  "ready",
  "failed",
]);

const ASSET_TYPE_LABELS: Record<DashboardAssetType, string> = {
  model: '3D model',
  image: 'image',
  pdf: 'PDF',
  video: 'video',
  cad: 'CAD drawing',
};

const ASSET_TYPE_ICONS: Record<DashboardAssetType, string> = {
  model: 'view_in_ar',
  image: 'image',
  pdf: 'picture_as_pdf',
  video: 'play_circle',
  cad: 'architecture',
};

const ASSET_TYPE_ACTIONS: Record<DashboardAssetType, string> = {
  model: 'Download',
  image: 'Download Image',
  pdf: 'Download PDF',
  video: 'Download Video',
  cad: 'Download CAD',
};

const ASSET_MODAL_TITLES: Record<DashboardAssetType, string> = {
  model: '3D Asset Details',
  image: 'Asset Details',
  pdf: 'Asset Details',
  video: 'Asset Details',
  cad: 'CAD Asset Details',
};

const ASSET_MODAL_PREVIEW_LABELS: Record<DashboardAssetType, string> = {
  model: 'Interactive 3D Preview',
  image: 'Image preview',
  pdf: 'PDF document preview',
  video: 'Video preview',
  cad: 'CAD drawing preview',
};

const ASSET_MODAL_SUMMARY_LABELS: Record<DashboardAssetType, string> = {
  model: '3D Model',
  image: 'PNG Image',
  pdf: 'PDF Document',
  video: 'Video File',
  cad: 'CAD Drawing',
};

const ASSET_MODAL_META_ICONS: Record<DashboardAssetType, string> = {
  model: 'view_in_ar',
  image: 'image',
  pdf: 'picture_as_pdf',
  video: 'database',
  cad: 'architecture',
};

const DASHBOARD_ASSET_DEFERRED_ACTION_NOTICE =
  'Seeded inventory assets still need a live project asset record before dashboard actions can call real backend URLs.';

const SUPPORTED_ASSET_TYPES = new Set<DashboardAssetType>([
  'model',
  'image',
  'pdf',
  'video',
  'cad',
]);

function normalizeSearchText(value: string): string {
  return value.toLowerCase().trim();
}

function parseSizeLabel(sizeLabel: string): number {
  const match = sizeLabel.trim().match(/^([\d.,]+)\s*(KB|MB|GB|TB)$/i);
  if (!match) return 0;

  const magnitude = Number.parseFloat(match[1].replace(/,/g, ''));
  if (!Number.isFinite(magnitude)) return 0;

  const unit = match[2].toUpperCase();
  const multipliers: Record<string, number> = {
    KB: 1024,
    MB: 1024 ** 2,
    GB: 1024 ** 3,
    TB: 1024 ** 4,
  };

  return magnitude * (multipliers[unit] ?? 0);
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '0 MB';
  }

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** exponent;
  const formatted = value >= 10 || exponent === 0 ? value.toFixed(0) : value.toFixed(1);
  return `${formatted} ${units[exponent]}`;
}

function buildSearchIndex(asset: DashboardAsset): string {
  return normalizeSearchText(
    [
      asset.name,
      asset.type,
      asset.description,
      asset.sizeLabel,
      asset.updatedAtLabel,
      ...asset.metadata.flatMap((entry) => [entry.label, entry.value]),
    ].join(' '),
  );
}

function isSupportedAssetType(value: unknown): value is DashboardAssetType {
  return typeof value === 'string' && SUPPORTED_ASSET_TYPES.has(value as DashboardAssetType);
}

function isSupportedReadiness(
  value: unknown,
): value is DashboardAssetReadinessStatus {
  return (
    typeof value === "string" &&
    SUPPORTED_READINESS.has(value as DashboardAssetReadinessStatus)
  );
}

function normalizeDashboardAssetRecord(record: DashboardAssetRecord | null | undefined): DashboardAsset | null {
  if (!record || typeof record !== 'object') {
    return null;
  }

  const id = typeof record.id === 'string' ? record.id.trim() : '';
  const name = typeof record.name === 'string' ? record.name.trim() : '';
  const sizeLabel = typeof record.sizeLabel === 'string' ? record.sizeLabel.trim() : '';
  const updatedAtLabel = typeof record.updatedAtLabel === 'string' ? record.updatedAtLabel.trim() : '';
  const description = typeof record.description === 'string' ? record.description.trim() : '';
  const readiness = isSupportedReadiness(record.readiness)
    ? record.readiness
    : "ready";

  if (!id || !name || !sizeLabel || !updatedAtLabel || !isSupportedAssetType(record.type)) {
    return null;
  }

  const metadata = Array.isArray(record.metadata)
    ? record.metadata
        .filter((entry): entry is { label: string; value: string } => {
          return Boolean(entry) && typeof entry.label === 'string' && typeof entry.value === 'string';
        })
        .map((entry) => ({ label: entry.label.trim(), value: entry.value.trim() }))
        .filter((entry) => entry.label.length > 0 && entry.value.length > 0)
    : [];

  return {
    id,
    name,
    type: record.type,
    readiness,
    sizeLabel,
    updatedAtLabel,
    description,
    metadata,
  };
}

export function normalizeDashboardAssetRecords(records: ReadonlyArray<DashboardAssetRecord | null | undefined>): DashboardAsset[] {
  return records.map((record) => normalizeDashboardAssetRecord(record)).filter((asset): asset is DashboardAsset => asset !== null);
}

const NORMALIZED_DASHBOARD_ASSETS = normalizeDashboardAssetRecords(DASHBOARD_ASSETS);

export function getDashboardAssetInventory(additionalAssets: ReadonlyArray<DashboardAssetRecord | null | undefined> = []): DashboardAssetInventory {
  const assets = normalizeDashboardAssetRecords(additionalAssets).concat(NORMALIZED_DASHBOARD_ASSETS);
  const countsByType = assets.reduce<Record<DashboardAssetType, number>>(
    (counts, asset) => {
      counts[asset.type] += 1;
      return counts;
    },
    { model: 0, image: 0, pdf: 0, video: 0, cad: 0 },
  );
  const totalBytes = assets.reduce((sum, asset) => sum + parseSizeLabel(asset.sizeLabel), 0);
  const countsByReadiness =
    assets.reduce<Record<DashboardAssetReadinessStatus, number>>(
      (counts, asset) => {
        counts[asset.readiness] += 1;
        return counts;
      },
      { staged: 0, processing: 0, ready: 0, failed: 0 },
    );

  return {
    assets,
    countsByType,
    countsByReadiness,
    storageUsedLabel: formatBytes(totalBytes),
    totalAssetsLabel: `${assets.length} asset${assets.length === 1 ? '' : 's'}`,
  };
}

export function filterDashboardAssets(
  assets: ReadonlyArray<DashboardAsset>,
  activeTab: DashboardAssetType,
  searchQuery: string,
): DashboardAsset[] {
  const normalizedQuery = normalizeSearchText(searchQuery);

  return assets.filter((asset) => {
    if (asset.type !== activeTab) {
      return false;
    }

    if (!normalizedQuery) {
      return true;
    }

    return buildSearchIndex(asset).includes(normalizedQuery);
  });
}

export function getDashboardAssetTypeLabel(type: DashboardAssetType): string {
  return ASSET_TYPE_LABELS[type];
}

export function getDashboardAssetIcon(type: DashboardAssetType): string {
  return ASSET_TYPE_ICONS[type];
}

export function getDashboardAssetActionLabel(asset: DashboardAsset): string {
  return ASSET_TYPE_ACTIONS[asset.type];
}

export function getDashboardAssetModalTitle(type: DashboardAssetType): string {
  return ASSET_MODAL_TITLES[type];
}

export function getDashboardAssetModalPreviewLabel(type: DashboardAssetType): string {
  return ASSET_MODAL_PREVIEW_LABELS[type];
}

export function getDashboardAssetModalSummaryLabel(asset: DashboardAsset): string {
  if (asset.type === 'image') {
    const fileExtension = asset.name.split('.').pop()?.trim().toLowerCase();
    if (fileExtension === 'jpg' || fileExtension === 'jpeg') {
      return 'JPG Image';
    }
  }

  return ASSET_MODAL_SUMMARY_LABELS[asset.type];
}

export function getDashboardAssetModalMetaIcon(type: DashboardAssetType): string {
  return ASSET_MODAL_META_ICONS[type];
}

export function getDashboardAssetTimestampLabel(asset: DashboardAsset): string {
  if (/^(uploaded|updated)\b/i.test(asset.updatedAtLabel)) {
    return asset.updatedAtLabel;
  }

  if (asset.type === 'pdf' || asset.type === 'video' || asset.type === 'cad') {
    return `Updated ${asset.updatedAtLabel}`;
  }

  return `Uploaded ${asset.updatedAtLabel}`;
}

export function getDashboardAssetDeferredActionNotice(): string {
  return DASHBOARD_ASSET_DEFERRED_ACTION_NOTICE;
}
